/// <amd-module name="Feed/Common/Protocols/UPack/RestClient/UPackApi" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   packaging\client\upack\webapi\clientgeneratorconfigs\genclient.json
 */

import { IVssPageContext, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";

import * as UPackApi from "Feed/Common/Protocols/UPack/Contracts/UPackApi";

import { IUPackApiRestClient } from "Feed/Common/Protocols/UPack/RestClient/UPackApi.Types";

export { IUPackApiRestClient } from "Feed/Common/Protocols/UPack/RestClient/UPackApi.Types";

class UPackApiRestClient extends RestClientBase implements IUPackApiRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Update several packages from a single feed in a single request. The updates to the packages do not happen atomically.
     * 
     * @param batchRequest - Information about the packages to update, the operation to perform, and its associated data.
     * @param feedId - Name or ID of the feed.
     * @param project - Project ID or project name
     */
    public async updatePackageVersions(
        batchRequest: UPackApi.UPackPackagesBatchRequest,
        feedId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/upack/packagesBatch",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: batchRequest
        });
    }

    /**
     * Delete or restore several package versions from the recycle bin.
     * 
     * @param batchRequest - Information about the packages to update, the operation to perform, and its associated data. \<c\>Operation\</c\> must be \<c\>PermanentDelete\</c\> or \<c\>RestoreToFeed\</c\>
     * @param feedId - Feed which contains the packages to update.
     * @param project - Project ID or project name
     */
    public async updateRecycleBinPackageVersions(
        batchRequest: UPackApi.UPackPackagesBatchRequest,
        feedId: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/upack/RecycleBin/packagesBatch",
            routeValues: {
                project: project,
                feedId: feedId
            },
            body: batchRequest
        });
    }

    /**
     * Delete a package version from the recycle bin.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async deletePackageVersionFromRecycleBin(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/upack/RecycleBin/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Get information about a package version in the recycle bin.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async getPackageVersionMetadataFromRecycleBin(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<UPackApi.UPackPackageVersionDeletionState> {

        return this.beginRequest<UPackApi.UPackPackageVersionDeletionState>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/upack/RecycleBin/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Restore a package version from the recycle bin to its associated feed.
     * 
     * @param packageVersionDetails - Set the 'Deleted' property to 'false' to restore the package.
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async restorePackageVersionFromRecycleBin(
        packageVersionDetails: UPackApi.UPackRecycleBinPackageVersionDetails,
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/upack/RecycleBin/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            },
            body: packageVersionDetails
        });
    }

    /**
     * Delete a package version from a feed's recycle bin.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async deletePackageVersion(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<UPackApi.Package> {

        return this.beginRequest<UPackApi.Package>({
            apiVersion: "5.0-preview.1",
            method: "DELETE",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/upack/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            }
        });
    }

    /**
     * Show information about a package version.
     * 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     * @param showDeleted - True to show information for deleted versions
     */
    public async getPackageVersion(
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string,
        showDeleted?: boolean
        ): Promise<UPackApi.Package> {

        const queryValues: any = {
            showDeleted: showDeleted
        };

        return this.beginRequest<UPackApi.Package>({
            apiVersion: "5.0-preview.1",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/upack/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            },
            queryParams: queryValues
        });
    }

    /**
     * Update information for a package version.
     * 
     * @param packageVersionDetails - 
     * @param feedId - Name or ID of the feed.
     * @param packageName - Name of the package.
     * @param packageVersion - Version of the package.
     * @param project - Project ID or project name
     */
    public async updatePackageVersion(
        packageVersionDetails: UPackApi.PackageVersionDetails,
        feedId: string,
        packageName: string,
        packageVersion: string,
        project?: string
        ): Promise<void> {

        return this.beginRequest<void>({
            apiVersion: "5.0-preview.1",
            method: "PATCH",
            routeTemplate: "{project}/_apis/packaging/feeds/{feedId}/upack/packages/{packageName}/versions/{packageVersion}",
            routeValues: {
                project: project,
                feedId: feedId,
                packageName: packageName,
                packageVersion: packageVersion
            },
            body: packageVersionDetails
        });
    }

}

export const UPackApiClientName = "Universal.IUPackApiRestClient";

export function getUPackApiClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): IUPackApiRestClient {
    return pageContext.getRestClient<IUPackApiRestClient>(UPackApiClientName, options);
}

RestClients.add(UPackApiClientName, { 
    factory: UPackApiRestClient,
    options: {
        resourceAreaId: "d397749b-f115-4027-b6dd-77a65dd10d21",
        serviceInstanceType: "00000030-0000-8888-8000-000000000000"
    }
});
